import React, { useEffect, useRef, useState } from "react";
import { Cropper, ReactCropperElement } from "react-cropper";
import { mdiRotateLeft, mdiRotateRight, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import ChunkedUploader from "common/ChunkUploader";
import Button from "components/Button";
import DropzoneFile from "components/DropzoneFile";
import Modal from "components/Modal";
import MultiSelect from "components/MultiSelect";
import Select from "components/Select";
import UploadDocumentsButton from "components/UploadDocumentsButton";
import { UPLOAD_CATEGORY } from "constants/base";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { FileUploadProvider, useUpload } from "context/FileUpload";
import { formatFileSize } from "helpers/formatFileSize";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IOption } from "models/Form";
import { IUpdatedFRAttachment } from "models/HaulPay";
import { deleteFRAttachmentAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

import "cropperjs/dist/cropper.css";

type PureUploadFileProps = {
  label?: string;
  prefixNode?: React.ReactNode;
  className?: string;
  categories?: IOption[];
  multiple?: boolean;
  visibility?: boolean;
  urlCallback: string;
  onUploadFinished: (files: any, fileId: string) => void;
  autoUpload?: boolean;
  customerId?: string;
  getPreviewUrl?: (url: string) => void;
  updatedFile?: IUpdatedFRAttachment[];
  setFile?: (file: IUpdatedFRAttachment[]) => void;
};

function PureFile({ file, onRemoveFile, onReplaceFile }: any) {
  const isImage = file.type.includes("image");
  const cropperRef = useRef<ReactCropperElement>(null);
  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    if (isImage) {
      const newUrl = URL.createObjectURL(file);
      setObjectUrl(newUrl);

      if (cropperRef.current?.cropper) {
        cropperRef.current.cropper.replace(newUrl);
      }

      return () => URL.revokeObjectURL(newUrl);
    }
  }, [file, isImage]);

  const getCroppedImage = async () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const croppedCanvas = cropper.getCroppedCanvas();

      const MAX_DIMENSION = 1024;
      const scaleFactor = Math.min(
        MAX_DIMENSION / croppedCanvas.width,
        MAX_DIMENSION / croppedCanvas.height
      );

      const scaledCanvas = document.createElement("canvas");
      scaledCanvas.width = croppedCanvas.width * scaleFactor;
      scaledCanvas.height = croppedCanvas.height * scaleFactor;

      const ctx = scaledCanvas.getContext("2d");
      ctx?.drawImage(croppedCanvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

      const dataUrl = scaledCanvas.toDataURL("image/jpeg", 0.8);

      const croppedImage = await fetch(dataUrl)
        .then((res) => res.blob())
        .then(
          (blob) =>
            new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now()
            })
        );

      onReplaceFile(croppedImage, file.id);
    }
  };

  const rotateToRight = () => {
    const cropper = cropperRef.current?.cropper;
    cropper?.rotate(90);
  };

  const rotateToLeft = () => {
    const cropper = cropperRef.current?.cropper;
    cropper?.rotate(-90);
  };

  return (
    <div className={styles.pureFile}>
      <span>
        {file.name} {formatFileSize(file.size)}
      </span>

      <div className={styles.progressWrapper}>
        <div className={styles.progress}>
          <div
            className={clsx(styles.currentProgress, {
              "!bg-success": file.progress >= 100,
              "bg-error": file.status === "error"
            })}
            style={{ width: `${file.progress}%` }}
          />
        </div>
        <button type="button" onClick={() => onRemoveFile(file.id)}>
          <Icon path={mdiTrashCanOutline} className={styles.closeIcon} />
        </button>
      </div>

      {isImage && (
        <Cropper
          src={objectUrl}
          className={styles.cropperWrapper}
          viewMode={2}
          initialAspectRatio={1}
          style={{ height: 400, width: "100%" }}
          guides={true}
          zoomOnTouch={true}
          autoCrop={true}
          ref={cropperRef}
        />
      )}

      {isImage ? (
        <div className="flex *:flex-1 gap-3 mt-5">
          <Button type="button" className={clsx("mobile:px-3")} onClick={rotateToLeft}>
            <Icon path={mdiRotateLeft} />
          </Button>
          <Button type="button" className={clsx("mobile:px-3")} onClick={rotateToRight}>
            <Icon path={mdiRotateRight} />
          </Button>
          <Button type="button" variant="outline" onClick={() => onReplaceFile(file, file.id)}>
            {"Don't Crop"}
          </Button>
          <Button type="button" onClick={getCroppedImage}>
            Crop
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function PureUploadFile({
  label,
  prefixNode,
  className,
  categories = UPLOAD_CATEGORY,
  multiple = false,
  visibility = false,
  urlCallback,
  onUploadFinished,
  autoUpload = false, // New prop to determine auto-upload on drop
  customerId = undefined,
  getPreviewUrl,
  updatedFile = [],
  setFile
}: Readonly<PureUploadFileProps>) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentType, setDocumentType] = useState<Array<IOption>>([]);
  const [visibleTo, setVisibleTo] = useState<string[]>([]);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const {
    files,
    addFile,
    setFileProgress,
    setFileStatus,
    updateFileCategory,
    updateFileVisibility,
    removeFile,
    emptyFiles
  } = useUpload();

  const categoriesHandler = async (categories: string[] | string, id: string) => {
    const categoryValue =
      multiple && Array.isArray(categories)
        ? (categories as string[]).join(",")
        : (categories as string);
    updateFileCategory(id, categoryValue);
  };

  const visibleToHandler = async (visibleTo: string[], id: string) => {
    updateFileVisibility(id, visibleTo);
  };

  const handleSingleUpload = async (file: any) => {
    const id = file.id;

    if (customerId === "") {
      onUploadFinished([file], id);
      setShowUploadModal(false);
      return;
    }

    setFileStatus(id, "uploading");
    const uploader = new ChunkedUploader({
      url: "/upload/chunk/",
      urlCallback: customerId ? `${urlCallback}/${customerId}/file/${id}` : `${urlCallback}/${id}`,
      file,
      onProgress: (progress) => setFileProgress(id, progress)
    });

    try {
      await uploader.upload();
      setFileStatus(id, "success");
      showSuccessMessage("File uploaded successfully.");
      onUploadFinished(
        [{ ...files[0], name: files[0].name, preview_url: uploader.previewUrl }] as any[],
        id
      );
      getPreviewUrl && getPreviewUrl(uploader.previewUrl);
      setShowUploadModal(false);
      if (file) {
        setFile?.([]);
        emptyFiles();
      }
    } catch (error) {
      setFileStatus(id, "error");
      showErrorMessage("Upload failed");
    }
  };

  const handleUpload = async (filesToUpload: any) => {
    if (!filesToUpload || filesToUpload.length === 0) {
      showErrorMessage("No files to upload.");
      return;
    }

    for (const file of filesToUpload) {
      if (file) {
        await handleSingleUpload(file);
      }
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      showErrorMessage("No files dropped.");
      return;
    }

    const isImage = acceptedFiles[0].type.includes("image");
    setIsImageUploaded(isImage);
    // Add the newly dropped files to the upload queue
    const newFiles = acceptedFiles.map((file) => addFile(file)); // Get the full FileProgress objects
    if (documentType.length) {
      newFiles.forEach((file) =>
        updateFileCategory(file.id, documentType.map((type) => type.label).join(","))
      );
    }

    if (autoUpload && newFiles.length > 0 && !isImage) {
      await handleUpload(newFiles); // Pass the full objects to handleUpload
    }
  };

  const onFileInputChange = async (event: any) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      showErrorMessage("No file selected.");
      return;
    }

    const isImage = selectedFile.type.includes("image");
    setIsImageUploaded(isImage);
    const newFile = addFile(selectedFile); // Get the full FileProgress object
    if (documentType.length) {
      updateFileCategory(newFile.id, documentType.map((type) => type.label).join(","));
    }

    if (autoUpload && !isImage) {
      await handleSingleUpload(newFile); // Pass the full object to handleSingleUpload
    }

    event.target.value = ""; // Reset the input field to allow reselecting the same file
  };

  // Manual upload click handler
  const handleUploadClick = async () => {
    try {
      if (files.length === 0) {
        showErrorMessage("No files to upload.");
        return;
      }

      if (updatedFile.length > 0) {
        const { frId, id: attachmentId } = updatedFile[0];
        if (!frId || !attachmentId) {
          showErrorMessage("Invalid attachment data.");
          return;
        }
        await deleteFRAttachmentAPI(frId, attachmentId);
      }

      const filesToUpload = files.filter((file) => file.status === "onProcess");

      if (filesToUpload.length === 0) {
        showErrorMessage("No files available for upload.");
        return;
      }
      // Update categories and visibility before upload
      filesToUpload.forEach((file) => {
        if (!autoUpload && !file.categories && updatedFile.length > 0) {
          categoriesHandler(updatedFile[0].category, file.id);
        }

        if (
          visibility &&
          (!file.visible_to || file.visible_to.length === 0) &&
          updatedFile.length > 0
        ) {
          visibleToHandler(updatedFile[0].visibleTo, file.id);
        }
      });
      await handleUpload(filesToUpload);
    } catch (error) {
      showErrorMessage("An error occurred during upload.");
    }
  };

  const handleOpenUploadModal = () => {
    setShowUploadModal(true);
  };

  const handleChangeDocumentType = (value: IOption[]) => {
    setDocumentType(value);
    files.forEach((file) =>
      categoriesHandler(
        value.map((item) => item.label),
        file.id
      )
    );
  };

  const handleChangeVisibleTo = (value: IOption) => {
    const visibleTo = value.value.split(",");
    setVisibleTo(visibleTo);
    files.forEach((file) => visibleToHandler(visibleTo, file.id));
  };
  const handleReplaceFile = (file: File, id: string) => {
    removeFile(id);
    addFile(file, true);
  };

  const onFileFromUrl = async (url: string, updatedFile: IUpdatedFRAttachment) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch file");

      const fileBlob = await response.blob();
      const file = new File([fileBlob], updatedFile.filename || "file_from_url.jpg", {
        type: fileBlob.type
      });
      const event = {
        target: {
          files: [file]
        }
      };

      onFileInputChange(event);
      const updatedFileCategory =
        typeof updatedFile.category === "string"
          ? updatedFile.category.split(",")
          : updatedFile.category;

      setDocumentType(categories.filter((item) => updatedFileCategory.includes(item.label)));
      setVisibleTo(updatedFile.visibleTo);
    } catch {
      /** empty */
    }
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDropButton = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      (async () => {
        await onDrop(Array.from(droppedFiles));
        handleOpenUploadModal();
        setDragActive(false);
      })();
    }
  };

  useEffect(() => {
    if (updatedFile?.length) {
      onFileFromUrl(updatedFile[0].downloadUrl, updatedFile[0]);
    }
  }, [updatedFile?.length]);

  useEffect(() => {
    if (!showUploadModal && customerId !== "") {
      setDocumentType([]);
      setVisibleTo([]);
      emptyFiles();
    }
  }, [showUploadModal, customerId]);

  useEffect(() => {
    if (customerId) {
      handleUploadClick();
    }
  }, [customerId]);
  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDropButton}
        className={`
        ${dragActive ? "bg-gray-800 border-dashed border-gray-700 border-4" : ""}
        p-4 rounded-lg transition-all border-gray-700
      `}>
        <UploadDocumentsButton
          label={label}
          prefixNode={prefixNode}
          className={className}
          onClick={handleOpenUploadModal}
        />
      </div>
      <Modal
        isOpen={showUploadModal || !!updatedFile?.length}
        closeModal={() => {
          setFile?.([]);
          emptyFiles();
          setShowUploadModal(false);
          setDocumentType([]);
          setVisibleTo([]);
          setIsImageUploaded(false);
        }}
        wrapperClass="!my-auto !py-4"
        dialogClass="!bg-background !p-0 !border-none !overflow-y-auto !min-h-72 justify-between"
        closeClass="!top-7 !right-6 !bg-transparent !text-placeholder"
        showCloseButton>
        <div className={styles.modalWrapper}>
          {files.length ? (
            files.map((file) => (
              <PureFile
                key={file.id}
                file={file}
                onRemoveFile={(id: string) => {
                  removeFile(id);
                  setIsImageUploaded(false);
                }}
                onReplaceFile={handleReplaceFile}
              />
            ))
          ) : (
            <DropzoneFile onDrop={onDrop} onChange={onFileInputChange} />
          )}
          {!autoUpload ? (
            <>
              {multiple ? (
                <MultiSelect
                  value={
                    documentType.length > 0
                      ? documentType
                      : categories.filter((option) =>
                          updatedFile[0]?.category?.includes(option.label)
                        )
                  }
                  onChange={handleChangeDocumentType}
                  options={categories}
                  placeholder="Select Document Type"
                  label="&nbsp;&nbsp;Document"
                  className="flex flex-col gap-2"
                  direction="up"
                  disabled={!files.length}
                />
              ) : (
                <Select
                  value={documentType[0]}
                  options={categories}
                  direction="up"
                  placeholder="Select Document Type"
                  label="&nbsp;&nbsp;Document"
                  className="flex flex-col gap-2"
                  btnClass="!py-2 h-11 !px-4 !text-sm"
                  arrowClass="!right-2 !text-text"
                  onChange={(value) => handleChangeDocumentType([value])}
                  disabled={!files.length}
                />
              )}
            </>
          ) : null}

          {visibility ? (
            <Select
              value={VISIBILITY_LIST.find((item) => item.value === visibleTo.join(","))}
              options={VISIBILITY_LIST}
              direction="up"
              placeholder="Visible To"
              label="&nbsp;&nbsp;Visible To"
              className="flex flex-col gap-2"
              btnClass="!py-2 h-11 !px-4 !text-sm"
              arrowClass="!right-2 !text-text"
              onChange={(value) => handleChangeVisibleTo(value)}
              disabled={!files.length}
            />
          ) : null}

          {!autoUpload || isImageUploaded ? (
            <Button
              type="button"
              rounded
              className={styles.uploadBtn}
              disabled={
                !updatedFile?.[0]?.category.length &&
                (!files.length ||
                  !documentType.length ||
                  (visibility ? !visibleTo.length : false)) &&
                (!isImageUploaded ||
                  !documentType.length ||
                  (visibility ? !visibleTo.length : false)) &&
                (!autoUpload || !isImageUploaded || !files.length)
              }
              onClick={handleUploadClick}>
              Upload
            </Button>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

export default function ChunkUploadFile(props: Readonly<PureUploadFileProps>) {
  return (
    <FileUploadProvider>
      <PureUploadFile {...props} />
    </FileUploadProvider>
  );
}
